import { create } from "zustand";

/**
 * Checkbox draft for the Welcome / terms screen. Lives in memory (not
 * persisted) so selections survive client-side navigation to a legal
 * document and back. Scoped to the current link so a different magic link
 * does not inherit another user's checks.
 */
interface TermsDraftState {
  scope: string | null;
  checkedById: Record<string, boolean>;
  isESignChecked: boolean;
  isTermsChecked: boolean;
  setEntryChecked: (linkId: string, entryId: string, checked: boolean) => void;
  setESignChecked: (linkId: string, checked: boolean) => void;
  setTermsChecked: (linkId: string, checked: boolean) => void;
  reset: () => void;
}

const empty = {
  scope: null as string | null,
  checkedById: {} as Record<string, boolean>,
  isESignChecked: false,
  isTermsChecked: false,
};

function withScope(
  state: TermsDraftState,
  linkId: string,
  patch: Partial<Pick<TermsDraftState, "checkedById" | "isESignChecked" | "isTermsChecked">>,
): Pick<TermsDraftState, "scope" | "checkedById" | "isESignChecked" | "isTermsChecked"> {
  if (state.scope === linkId) {
    return {
      scope: linkId,
      checkedById: state.checkedById,
      isESignChecked: state.isESignChecked,
      isTermsChecked: state.isTermsChecked,
      ...patch,
    };
  }
  return { ...empty, ...patch, scope: linkId };
}

export const useTermsDraftStore = create<TermsDraftState>((set) => ({
  ...empty,
  setEntryChecked: (linkId, entryId, checked) =>
    set((state) => {
      const base = state.scope === linkId ? state.checkedById : {};
      return withScope(state, linkId, {
        checkedById: { ...base, [entryId]: checked },
      });
    }),
  setESignChecked: (linkId, checked) =>
    set((state) => withScope(state, linkId, { isESignChecked: checked })),
  setTermsChecked: (linkId, checked) =>
    set((state) => withScope(state, linkId, { isTermsChecked: checked })),
  reset: () => set(() => ({ ...empty })),
}));
